import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HelpCircle,
  Mail,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Keyboard,
  Lock,
  Cloud,
  Smartphone,
  Trash2,
  RefreshCw
} from 'lucide-react';
import LegalLayout from '../LegalLayout';

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  const magentaColor = '#d100a3';

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)'
      }}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left transition-colors"
        style={{ color: 'var(--text-primary)' }}
      >
        <span className="font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: magentaColor }} />
        ) : (
          <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
        )}
      </button>
      {isOpen && (
        <div
          className="px-6 pb-4"
          style={{ color: 'var(--text-secondary)' }}
        >
          {answer}
        </div>
      )}
    </div>
  );
};

const Support = () => {
  const magentaColor = '#d100a3';
  const [openFAQ, setOpenFAQ] = useState(0);

  const faqs = [
    {
      icon: <Keyboard className="w-5 h-5" />,
      question: "How do I enable the ClipPilot keyboard?",
      answer: (
        <div className="space-y-2">
          <p>To enable the ClipPilot keyboard:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open <strong>Settings</strong> on your iPhone/iPad</li>
            <li>Go to <strong>General → Keyboard → Keyboards</strong></li>
            <li>Tap <strong>Add New Keyboard</strong></li>
            <li>Select <strong>ClipPilot</strong></li>
            <li>Tap on <strong>ClipPilot</strong> again and enable <strong>Allow Full Access</strong></li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Note: Full Access is required for the keyboard to access your saved clips. The keyboard does not log keystrokes.
          </p>
        </div>
      )
    },
    {
      icon: <Lock className="w-5 h-5" />,
      question: "I forgot my PIN. How can I reset it?",
      answer: (
        <div className="space-y-2">
          <p>If you've forgotten your PIN and cannot use Face ID/Touch ID:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Unfortunately, for security reasons, there is no way to recover a forgotten PIN</li>
            <li>You will need to uninstall and reinstall the App</li>
            <li>This will delete all locally stored clipboard data</li>
            <li>If you have iCloud sync enabled (Pro), your data will be restored after reinstalling</li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Tip: Enable Face ID or Touch ID as your primary authentication method to avoid this issue.
          </p>
        </div>
      )
    },
    {
      icon: <Cloud className="w-5 h-5" />,
      question: "How does iCloud sync work?",
      answer: (
        <div className="space-y-2">
          <p>iCloud sync is a Pro feature that automatically syncs your clipboard history across all your Apple devices:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Data is encrypted end-to-end using Apple's CloudKit</li>
            <li>Sync happens automatically when connected to the internet</li>
            <li>New clips appear on all devices within seconds</li>
            <li>Deleting a clip removes it from all synced devices</li>
          </ul>
          <p className="mt-3"><strong>To enable iCloud sync:</strong></p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Purchase ClipPilot Pro (Lifetime or Yearly)</li>
            <li>Go to Settings in the App</li>
            <li>Toggle on "iCloud Sync"</li>
            <li>Ensure iCloud is enabled in your device settings</li>
          </ol>
        </div>
      )
    },
    {
      icon: <Smartphone className="w-5 h-5" />,
      question: "Why isn't ClipPilot capturing my clipboard?",
      answer: (
        <div className="space-y-2">
          <p>ClipPilot can only capture clipboard content when the app is in the foreground. This is an iOS privacy restriction.</p>
          <p className="mt-2"><strong>Tips for better clipboard capture:</strong></p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Keep ClipPilot open or in Split View while copying content</li>
            <li>Switch to ClipPilot immediately after copying something important</li>
            <li>Use the keyboard extension to quickly access and save clips</li>
          </ul>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Note: This is a limitation of iOS, not ClipPilot. Apple restricts background clipboard access for privacy reasons.
          </p>
        </div>
      )
    },
    {
      icon: <Trash2 className="w-5 h-5" />,
      question: "How do I delete my clipboard history?",
      answer: (
        <div className="space-y-2">
          <p>You can delete clips in several ways:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Single clip:</strong> Swipe left on any clip and tap Delete</li>
            <li><strong>All clips:</strong> Go to Settings → Clear All History</li>
            <li><strong>Expired OTPs:</strong> These are automatically deleted after 10 minutes</li>
          </ul>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            If you have iCloud sync enabled, deleted clips will be removed from all your devices.
          </p>
        </div>
      )
    },
    {
      icon: <RefreshCw className="w-5 h-5" />,
      question: "How do I restore my Pro purchase?",
      answer: (
        <div className="space-y-2">
          <p>If you've previously purchased ClipPilot Pro:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open ClipPilot</li>
            <li>Go to <strong>Settings → ClipPilot Pro</strong></li>
            <li>Tap <strong>Restore Purchases</strong></li>
            <li>Sign in with the same Apple ID used for the original purchase</li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            If restoration fails, ensure you're signed into the correct Apple ID and have an active internet connection.
          </p>
        </div>
      )
    }
  ];

  return (
    <LegalLayout app="ClipPilot" docType="Support" icon={HelpCircle}>
        {/* Contact Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <a
            href="mailto:support@feraltechlabs.co.in"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = magentaColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${magentaColor}20` }}
              >
                <Mail className="w-6 h-6" style={{ color: magentaColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Email Support
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  support@feraltechlabs.co.in
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              We typically respond within 24-48 hours
            </p>
          </a>

          <a
            href="mailto:support@feraltechlabs.co.in?subject=ClipPilot%20Bug%20Report"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = magentaColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${magentaColor}20` }}
              >
                <MessageSquare className="w-6 h-6" style={{ color: magentaColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Report a Bug
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Help us improve ClipPilot
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Include your iOS version and steps to reproduce
            </p>
          </a>
        </div>

        {/* FAQ Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>

        {/* App Info */}
        <div
          className="rounded-xl p-6"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            App Information
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>App Name:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>ClipPilot</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Developer:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Feral Techlab</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Platform:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>iOS 16.0+</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Category:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Productivity</span>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link
            to="/apps/clippilot/privacy"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = magentaColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Privacy Policy
          </Link>
          <span style={{ color: 'var(--text-tertiary)' }}>•</span>
          <Link
            to="/apps/clippilot/terms"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = magentaColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Terms & Conditions
          </Link>
        </div>
    </LegalLayout>
  );
};

export default Support;
